import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Lightbulb,
  Plus,
  ThumbsUp,
  CheckCircle2,
  Clock,
  XCircle,
  Sparkles,
  Video,
  MessageSquare,
  TrendingUp,
  Trash2
} from 'lucide-react';

const IdeasPage = () => {
  const { user, api, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [newIdea, setNewIdea] = useState({
    title: '',
    description: '',
    category: 'general'
  });

  const categories = [
    { value: 'general', label: 'Général', icon: <Lightbulb className="w-4 h-4" /> },
    { value: 'webinaire', label: 'Sujet de webinaire', icon: <Video className="w-4 h-4" /> },
    { value: 'formation', label: 'Formation', icon: <Sparkles className="w-4 h-4" /> },
    { value: 'communaute', label: 'Communauté', icon: <MessageSquare className="w-4 h-4" /> },
    { value: 'amelioration', label: 'Amélioration', icon: <TrendingUp className="w-4 h-4" /> }
  ];

  const statusConfig = {
    pending: { label: 'En attente', icon: <Clock className="w-4 h-4" />, color: 'bg-yellow-500/10 text-yellow-500' },
    approved: { label: 'Approuvée', icon: <CheckCircle2 className="w-4 h-4" />, color: 'bg-green-500/10 text-green-500' },
    implemented: { label: 'Implémentée', icon: <Sparkles className="w-4 h-4" />, color: 'bg-primary/10 text-primary' },
    rejected: { label: 'Refusée', icon: <XCircle className="w-4 h-4" />, color: 'bg-red-500/10 text-red-500' }
  };

  useEffect(() => {
    fetchIdeas();
  }, []);

  const fetchIdeas = async () => {
    try {
      const response = await api.get('/ideas');
      setIdeas(response.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIdea = async (e) => {
    e.preventDefault();
    if (!newIdea.title || !newIdea.description) {
      toast.error('Remplissez tous les champs');
      return;
    }
    try {
      await api.post('/ideas', newIdea);
      toast.success('💡 Idée soumise !');
      setCreateOpen(false);
      setNewIdea({ title: '', description: '', category: 'general' });
      fetchIdeas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleVote = async (ideaId) => {
    try {
      const response = await api.post(`/ideas/${ideaId}/vote`);
      toast.success(response.data.voted ? '👍 Vote ajouté !' : 'Vote retiré');
      fetchIdeas();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleUpdateStatus = async (ideaId, status) => {
    try {
      await api.put(`/ideas/${ideaId}/status?status=${status}`);
      toast.success('Statut mis à jour');
      fetchIdeas();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteIdea = async (ideaId) => {
    if (!confirm('Supprimer cette idée ?')) return;
    try {
      await api.delete(`/ideas/${ideaId}`);
      toast.success('Idée supprimée');
      fetchIdeas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const filteredIdeas = useMemo(() => {
    return ideas
      .filter(idea => {
        if (filter === 'all') return true;
        if (filter === 'webinaire') return idea.category === 'webinaire';
        return idea.status === filter;
      })
      .slice()
      .sort((a, b) => b.vote_count - a.vote_count);
  }, [ideas, filter]);

  const getCategoryIcon = (category) => {
    const found = categories.find(c => c.value === category);
    return found?.icon || <Lightbulb className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Lightbulb className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement des idées...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="ideas-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center gold-glow">
                <Lightbulb className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl">Boîte à Idées</h1>
                <p className="text-sm text-muted-foreground">Proposez et votez pour les meilleures idées</p>
              </div>
            </div>
          </div>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground gold-glow" data-testid="submit-idea-btn">
                <Plus className="w-4 h-4 mr-2" />
                Proposer une idée
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-primary" />
                  Proposer une idée
                </DialogTitle>
                <DialogDescription>Votre idée sera soumise au vote de la communauté</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateIdea} className="space-y-4">
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select 
                    value={newIdea.category} 
                    onValueChange={(v) => setNewIdea({ ...newIdea, category: v })}
                  >
                    <SelectTrigger className="bg-secondary/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          <div className="flex items-center gap-2">
                            {cat.icon}
                            {cat.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Titre de l'idée *</Label>
                  <Input 
                    value={newIdea.title} 
                    onChange={(e) => setNewIdea({ ...newIdea, title: e.target.value })} 
                    placeholder="Ex: Webinaire sur la fiscalité" 
                    className="bg-secondary/50"
                    data-testid="idea-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea 
                    value={newIdea.description} 
                    onChange={(e) => setNewIdea({ ...newIdea, description: e.target.value })} 
                    placeholder="Décrivez votre idée en détail..."
                    className="bg-secondary/50"
                    rows={4}
                    data-testid="idea-description-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
                  <Button type="submit" className="bg-primary text-primary-foreground" data-testid="confirm-idea-btn">
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Soumettre
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Filters */}
        <Tabs value={filter} onValueChange={setFilter} className="mb-6">
          <TabsList className="bg-secondary/50 flex-wrap">
            <TabsTrigger value="all">Toutes</TabsTrigger>
            <TabsTrigger value="webinaire" className="text-purple-500">
              <Video className="w-4 h-4 mr-1" />
              Webinaires
            </TabsTrigger>
            <TabsTrigger value="pending">En attente</TabsTrigger>
            <TabsTrigger value="approved">Approuvées</TabsTrigger>
            <TabsTrigger value="implemented">Implémentées</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Ideas List */}
        {filteredIdeas.length === 0 ? (
          <Card className="card-base text-center py-12">
            <CardContent>
              <Lightbulb className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-xl mb-2">Aucune idée pour le moment</h3>
              <p className="text-muted-foreground mb-4">Soyez le premier à proposer une idée !</p>
              <Button onClick={() => setCreateOpen(true)} className="bg-primary text-primary-foreground gold-glow">
                <Plus className="w-4 h-4 mr-2" />
                Proposer une idée
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredIdeas.map((idea) => (
              <Card key={idea.id} className="card-base" data-testid={`idea-${idea.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">
                          {getCategoryIcon(idea.category)}
                          <span className="ml-1">{categories.find(c => c.value === idea.category)?.label || idea.category}</span>
                        </Badge>
                        <Badge className={statusConfig[idea.status]?.color}>
                          {statusConfig[idea.status]?.icon}
                          <span className="ml-1">{statusConfig[idea.status]?.label}</span>
                        </Badge>
                      </div>
                      <CardTitle className="text-lg">{idea.title}</CardTitle>
                      <CardDescription className="text-xs mt-1">
                        Proposée par {idea.user_name} • {new Date(idea.created_at).toLocaleDateString('fr-FR')}
                      </CardDescription>
                    </div>
                    
                    {/* Vote Button */}
                    <div className="flex items-center gap-2">
                      <Button 
                        variant={idea.votes?.includes(user.id) ? "default" : "outline"}
                        size="lg"
                        className={`flex flex-col items-center gap-1 h-auto py-2 px-4 ${idea.votes?.includes(user.id) ? 'bg-primary text-primary-foreground' : ''}`}
                        onClick={() => handleVote(idea.id)}
                        data-testid={`vote-btn-${idea.id}`}
                      >
                        <ThumbsUp className={`w-5 h-5 ${idea.votes?.includes(user.id) ? 'fill-current' : ''}`} />
                        <span className="text-lg font-bold">{idea.vote_count || 0}</span>
                      </Button>
                      
                      {/* Delete Button - Admin or Owner */}
                      {(isAdmin || idea.user_id === user.id) && (
                        <Button 
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:bg-red-500/10"
                          onClick={() => handleDeleteIdea(idea.id)}
                          data-testid={`delete-idea-${idea.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground whitespace-pre-wrap">{idea.description}</p>
                  
                  {/* Admin Actions */}
                  {isAdmin && idea.status === 'pending' && (
                    <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-green-500 hover:bg-green-500/10"
                        onClick={() => handleUpdateStatus(idea.id, 'approved')}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Approuver
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-primary hover:bg-primary/10"
                        onClick={() => handleUpdateStatus(idea.id, 'implemented')}
                      >
                        <Sparkles className="w-4 h-4 mr-1" />
                        Marquer implémentée
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-500 hover:bg-red-500/10"
                        onClick={() => handleUpdateStatus(idea.id, 'rejected')}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Refuser
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default IdeasPage;
